import {
  Component,
  Input,
  forwardRef,
  ChangeDetectorRef,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

export type LangKey = 'en' | 'ar';

export interface LangInputValue {
  en: string;
  ar: string;
}

/**
 * إنبوت واحد عادي.
 * لو محدد [bilingual]="true" بيتحول تلقائي لإنبوت بتاب عربي/انجليزي،
 * وقيمته بترجع {en, ar} بدل ما ترجع string عادي.
 */
@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  /** Label shown before the input, e.g. "Name" */
  @Input() label = '';

  /** لو true: الإنبوت بيبقى فيه تابينج EN/AR. لو false (الافتراضي): إنبوت عادي واحد */
  @Input() bilingual = false;

  /** Placeholder للحالة العادية (single input) */
  @Input() placeholder = '';

  /** Placeholder لكل لغة لما bilingual=true */
  @Input() placeholderEn = '';
  @Input() placeholderAr = '';

  /** Is the field required (adds the red * like in the design) */
  @Input() required = false;

  /** Which tab is active by default (لما bilingual=true) */
  @Input() defaultLang: LangKey = 'en';

  /** Input type, e.g. text / email / number */
  @Input() type: 'text' | 'email' | 'number' = 'text';

  activeLang: LangKey = this.defaultLang;

  singleValue = '';

  value: LangInputValue = { en: '', ar: '' };

  disabled = false;

  private onChange: (val: string | LangInputValue) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.activeLang = this.defaultLang;
  }

  switchTab(lang: LangKey): void {
    if (this.disabled) return;
    this.activeLang = lang;
  }

  onSingleInputChange(val: string): void {
    this.singleValue = val;
    this.onChange(this.singleValue);
    this.onTouched();
  }

  onLangInputChange(lang: LangKey, val: string): void {
    this.value = { ...this.value, [lang]: val };
    this.onChange(this.value);
    this.onTouched();
  }

  get currentPlaceholder(): string {
    if (!this.bilingual) return this.placeholder;
    return this.activeLang === 'en'
      ? this.placeholderEn || 'English Name'
      : this.placeholderAr || 'اسم عربي';
  }

  get currentDir(): 'ltr' | 'rtl' {
    return this.activeLang === 'en' ? 'ltr' : 'rtl';
  }

  // ===== ControlValueAccessor =====
  writeValue(val: string | LangInputValue): void {
    if (this.bilingual) {
      this.value = (val as LangInputValue) || { en: '', ar: '' };
    } else {
      this.singleValue = (val as string) || '';
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: string | LangInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}